"use client";

import React, { FC, memo, useMemo, useCallback, useState } from "react";
import { cn } from "utils";
import { useMemoizedFn } from "ahooks";
import { useQueryClient } from "@tanstack/react-query";
import log from "logbeacon";
import { CategoryCard } from "./CategoryCard";
import { useWatchlistAddAssets } from "./useWatchlistAddAssets";
import { useWatchlistAssets } from "./useWatchlistAssets";
import Translations from "@translate/components/Watchlist/WatchlistAddAssets/WatchlistAddAssets";
import { I18nUtil } from "@utils";
import { MenuSearchModalCreateAble } from "@/src/components/Menu/MenuSearchModal";
import { favoritesCenter } from "@/src/store/favorite";
import {
  PositionAssetTypeEnum,
  AddPositionAsset,
} from "@/src/features/api/memberApi";
import { QUERY_KEYS } from "@config";

const I18n = I18nUtil.createScoped(Translations);

type WatchlistAddAssetsProps = {
  className?: string;
};

/**
 * WatchlistAddAssets 主组件
 * 显示添加资产页面，包含 Stocks 和 Crypto 两个分类
 * 用于 watchlist 空状态时展示
 *
 * @param props WatchlistAddAssetsProps
 * @returns React.ReactElement
 */
export const WatchlistAddAssets: FC<WatchlistAddAssetsProps> = memo(
  ({ className }) => {
    const { stocks: recommendedStocks, crypto: recommendedCrypto } =
      useWatchlistAssets();
    const queryClient = useQueryClient();

    /**
     * 合并所有资产，用于全选/反选功能
     */
    const initialSelectedIds = useMemo(
      () => ({
        stocks: recommendedStocks.map((asset) => asset.id),
        crypto: recommendedCrypto.map((asset) => asset.id),
      }),
      [recommendedStocks, recommendedCrypto],
    );

    const { selectedByCategory, selectedCount, handleSelectionChange } =
      useWatchlistAddAssets({
        initialSelectedIds,
        onSelectionChange: () => {
          // 选择变化时的回调，可以在这里处理额外的逻辑
        },
      });

    /**
     * 处理中状态：跟踪批量添加操作是否正在进行
     */
    const [isAdding, setIsAdding] = useState(false);

    /**
     * 处理添加按钮点击
     * 批量添加选中的资产到收藏夹，使用 batchAddAssets 方法
     */
    const handleAddClick = useCallback(async () => {
      if (isAdding) {
        return; // 防止重复点击
      }
      setIsAdding(true);
      try {
        // 1. 获取用户的收藏夹ID
        const foldersResult = await favoritesCenter.fetchUserFolders({
          limit: 1,
          forceRefresh: false,
        });

        if (!foldersResult.success || !foldersResult.data?.length) {
          log.error(
            "[WatchlistAddAssets] 获取收藏夹失败",
            foldersResult.error || "未找到收藏夹",
          );
          setIsAdding(false);
          return;
        }
        const folderId = foldersResult.data[0].id;
        if (!folderId) {
          log.error("[WatchlistAddAssets] 收藏夹ID为空");
          setIsAdding(false);
          return;
        }

        // 2. 构建 AddPositionAsset 数组，合并 stocks 和 crypto
        const assets: AddPositionAsset[] = [];

        // 2.1 添加 Stocks 分类（资产类型为 STOCK）
        if (selectedByCategory.stocks.length > 0) {
          selectedByCategory.stocks.forEach((assetId) => {
            assets.push({
              asset_id: assetId,
              asset_type: PositionAssetTypeEnum.STOCK,
            });
          });
        }

        // 2.2 添加 Crypto 分类（资产类型为 TOKEN）
        if (selectedByCategory.crypto.length > 0) {
          selectedByCategory.crypto.forEach((assetId) => {
            assets.push({
              asset_id: assetId,
              asset_type: PositionAssetTypeEnum.TOKEN,
            });
          });
        }

        // 3. 如果没有选中的资产，直接返回
        if (assets.length === 0) {
          log.warn("[WatchlistAddAssets] 没有选中的资产");
          setIsAdding(false);
          return;
        }

        // 4. 批量添加资产
        const result = await favoritesCenter.batchAddAssets(assets, folderId);
        if (result.success) {
          log.info(
            `[WatchlistAddAssets] 成功添加 ${result.data?.count || 0} 个资产`,
          );
          // 刷新 watchlist 数据
          queryClient.invalidateQueries({
            queryKey: [QUERY_KEYS.TOKEN_DASHBOARD, "tokenseek", "dashboard"],
          });
          // 刷新 portfolio 资产数据，用于更新 isEmpty 状态
          queryClient.invalidateQueries({
            queryKey: [QUERY_KEYS.PORTFOLIO_ASSETS_INFINITE],
          });
        } else {
          log.error(`[WatchlistAddAssets] 批量添加资产失败: ${result.error}`);
        }
      } catch (error) {
        log.error("[WatchlistAddAssets] 批量添加资产异常", error);
      } finally {
        setIsAdding(false);
      }
    }, [selectedByCategory, isAdding, queryClient]);

    /**
     * 处理添加其他资产按钮点击
     */
    const handleAddOthersClick = useMemoizedFn(async () => {
      await MenuSearchModalCreateAble({
        type: "portfolio",
      });
    });

    return (
      <div
        className={cn(
          "flex flex-col gap-6 md:gap-8 items-end",
          "pb-0 px-0 pt-0",
          className,
        )}
        data-tracking-scope="watchlist-add-assets"
      >
        {/* 标题和描述区域 */}
        <div className="flex flex-col gap-2 items-start w-full">
          {/* 标题 */}
          <h2
            aria-label="Watchlist Add Assets h2"
            data-openreplay-label="Watchlist Add Assets h2 Semantic"
            id="sem-watchlist-add-assets-h2"
            className="headline_regular_m md:headline_regular_s text-content_primary"
          >
            {I18n.t("Add Assets")}
          </h2>
          {/* 描述 */}
          <p className="body_l text-content_secondary whitespace-pre-wrap">
            {I18n.t(
              "Go on, add more. I'll make sure it all fits perfectly into your strategy, and I'll understand your investment style even better.",
            )}
          </p>
        </div>

        {/* 资产列表区域 */}
        <div className="flex gap-4 md:gap-6 items-start w-full">
          {/* Stocks 分类 */}
          <CategoryCard
            title={I18n.t("Stocks")}
            category="stocks"
            assets={recommendedStocks}
            selectedIds={selectedByCategory.stocks}
            onSelectionChange={handleSelectionChange}
          />

          {/* Crypto 分类 */}
          <CategoryCard
            title={I18n.t("Crypto")}
            category="crypto"
            assets={recommendedCrypto}
            selectedIds={selectedByCategory.crypto}
            onSelectionChange={handleSelectionChange}
          />
        </div>

        {/* 底部按钮区域 */}
        <div className="flex flex-col-reverse md:flex-row gap-4 items-stretch md:items-start w-full md:w-auto">
          {/* Add Others 按钮 */}
          <button
            aria-label="Watchlist Add Assets"
            data-openreplay-label={`Watchlist Add Assets Button | ${I18n.t("Add Others")}`}
            id="btn-watchlist-add-assets"
            type="button"
            disabled={isAdding}
            onClick={handleAddOthersClick}
            className="button secondary_button button_m h-11 md:h-9 w-full md:min-w-[80px] md:w-auto px-3 py-2 rounded-2 border border-border_white_transparent20 text-content_primary font-bold"
          >
            {I18n.t("Add Others")}
          </button>

          {/* Add (数量) 按钮 */}
          <button
            aria-label="Action"
            data-openreplay-label={`Action Button | ${I18n.t("Add")} (${selectedCount})`}
            id="btn-action-watchlistaddassets-watchlistaddass-6"
            type="button"
            onClick={handleAddClick}
            disabled={selectedCount === 0 || isAdding}
            className="button primary_button button_m h-11 md:h-9 w-full md:min-w-[80px] md:w-auto px-3 py-2 rounded-2 bg-background_white_overlay100 text-content_inverse_primary font-bold"
          >
            {I18n.t("Add")} ({selectedCount})
          </button>
        </div>
      </div>
    );
  },
);

WatchlistAddAssets.displayName = "WatchlistAddAssets";
